<template>
    <div class="container" v-if="isDataReady">
        <v-title :breadcrumb="breadcrumb">
            {{isFormUpdating ? 'Edit' : 'Add'}} Shipping Zone
            <div slot="actions">
                <v-cancel :defaultRoute="{name: 'shipping-zones'}"></v-cancel>
                <r-button type="primary" @click="save()" :loading="isLoading">Save Shipping Zone</r-button>
            </div>
        </v-title>
        <v-error-messages
            :has-frontend-errors="$v.shippingZone.$error"
            :server-error="serverError"
            class="stack-l">
            <li class="list-item" v-if="!$v.shippingZone.id.required">Shipping zone requires an ID</li>
            <li class="list-item" v-if="!$v.shippingZone.name.required">Shipping zone requires a name</li>
            <li class="list-item" v-if="!isRestOfTheWorld && !$v.shippingZone.countries.required">Shipping zone requires a least one country</li>
            <li class="list-item" v-if="!$v.shippingZone.rates.required">Shipping zone requires a least one rate</li>
        </v-error-messages>
        <v-inline-notifications
            v-if="repeatMessage && !($v.shippingZone.$error || serverError)"
            type="positive"
            title="Shipping Zone successfully created"
            class="stack-l">
            <span slot="content">
                You can add another shipping zone right away or go back to the <router-link :to="{name: 'shipping-zones'}">shipping zones list</router-link>?
            </span>
        </v-inline-notifications>
        <v-form-tile>
            <h2 slot="title">Zone Information</h2>
            <div slot="content" class="tile">
                <div class="tile-content">
                    <div class="grid-content">
                        <div class="grid-item grid-item-half grid-item-break-half">
                            <r-input
                                class="slug-source"
                                label="Name"
                                v-model="shippingZone.name"
                                :auto-focus="true"
                                :validate="$v.shippingZone.name"
                                help-text="A descriptive name for this shipping zone"
                            />
                        </div>
                        <div class="grid-item grid-item-half grid-item-break-half">
                            <r-input
                                class="slug-target"
                                label="ID"
                                v-model="shippingZone.id"
                                :validate="$v.shippingZone.id"
                                :disabled="isFormUpdating"
                                help-text="The ID must be unique and is used to identify the shipping zone in our API."
                            />
                        </div>
                    </div>
                </div>
            </div>
        </v-form-tile>
        <div class="divider"></div>
        <v-form-tile>
            <h2 slot="title">Countries</h2>
            <p slot="description">Countries covered by the shipping zone. A country can only belong to one shipping zone (no overlapping).</p>
            <div slot="content" class="tile">
                <div class="tile-content">
                    <template v-if="selectedCountriesFull.length">
                        <div class="grid-content">
                            <div class="grid-item">
                                <ul class="list list-group list-divided">
                                    <li class="list-item" v-for="country in selectedCountriesFull">
                                        <v-flags class="no-flex inline-s" :country-code="country.countryCode"/>
                                        <p class="inline-s">{{country.countryName}}</p>
                                        <v-icon-button
                                            class="no-padding no-flex"
                                            size="small"
                                            type="basic"
                                            @click="removeSelectedCountry(country)">
                                            <v-icon icon="trash"/>
                                        </v-icon-button>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </template>
                    <template v-else-if="isRestOfTheWorld">
                        <ul class="list list-group list-divided">
                            <li class="list-item">
                                <v-icon icon="world" class="no-flex inline-s"/>
                                <p class="inline-s">Rest of the World</p>
                                <v-icon-button
                                    class="no-padding no-flex"
                                    size="small"
                                    type="basic"
                                    @click="removeRestOfTheWorld()">
                                    <v-icon icon="trash"/>
                                </v-icon-button>
                            </li>
                        </ul>
                    </template>
                    <template v-else>
                        <p class="text-muted stack-m">This shipping zone has no countries added.</p>
                    </template>
                    <div class="grid-content">
                        <div class="grid-item">
                            <r-button v-if="!isRestOfTheWorld" @click="showModal('Countries')" :validate="$v.shippingZone.countries" type="link">Add Countries</r-button>
                            <v-select-countries
                                :show="showCountriesModal"
                                :selectedCountries="shippingZone.countries"
                                :other-zones="otherZones"
                                @selected="updateSelectedCountries"
                                @close="closeModal('Countries')"/>
                        </div>
                    </div>
                </div>
            </div>
        </v-form-tile>
        <div class="divider"></div>
        <v-form-tile>
            <h2 slot="title">Rates</h2>
            <p slot="description">Add rate based on the price of a customer's order.</p>
            <div slot="content" class="tile">
                <div class="tile-content">
                    <template v-if="shippingZone.rates.length">
                        <div class="grid-content">
                            <div class="grid-item">
                                <repeater :list="shippingZone.rates">
                                    <template slot="headers">
                                        <th>Name</th>
                                        <th>Minimum Subtotal</th>
                                        <th>Maximum Subtotal</th>
                                        <th>Price</th>
                                        <th>Currency</th>
                                        <th></th>
                                    </template>
                                    <template slot="iteratee" slot-scope="{item}">
                                        <td>{{item.name}}</td>
                                        <td>{{item.minOrderSubtotal | monetizeExplicit(item.currency)}}</td>
                                        <td>{{displayNoLimit(item.maxOrderSubtotal,item.currency)}}</td>
                                        <td>{{item.price | monetizeExplicit(item.currency)}}</td>
                                        <td>{{item.currency}}</td>
                                        <td class="text-right">
                                            <v-icon-button
                                                class="no-flex"
                                                tooltip="Edit"
                                                size="small"
                                                type="basic"
                                                @click="editShippingRate(item)"
                                            >
                                                <v-icon icon="edit" type="basic"/>
                                            </v-icon-button>
                                            <v-icon-button
                                                class="no-flex"
                                                tooltip="Delete"
                                                size="small"
                                                type="basic"
                                                @click="removeShippingRate(item)"
                                            >
                                                <v-icon icon="trash"/>
                                            </v-icon-button>
                                        </td>
                                    </template>
                                </repeater>
                            </div>
                        </div>
                        <rates-modal
                            v-if="showEditRateModal"
                            @input="addShippingRate"
                            :rate-to-edit="rateToEdit"
                            @close="closeModal('EditRate')"/>
                    </template>
                    <template v-else>
                        <p class="text-muted stack-m">This shipping zone has no rates added.</p>
                    </template>
                    <div class="grid-content">
                        <div class="grid-item">
                            <r-button @click="showModal('Rates')" type="link">Add Rates</r-button>
                            <rates-modal
                                v-if="showRatesModal"
                                @input="addShippingRate"
                                @close="closeModal('Rates')"/>
                        </div>
                    </div>
                </div>
            </div>
        </v-form-tile>
        <div class="divider"></div>
        <div class="grid-fitted stack-l">
            <div class="grid-item text-right equal-widths-elements-mobile">
                <v-cancel :defaultRoute="{name: 'shipping-zones'}"></v-cancel>
                <r-button type="primary" @click="save()" :loading="isLoading">Save Shipping Zone</r-button>
            </div>
        </div>
    </div>
</template>

<script>
    import COUNTRIES from '@/data/countries';
    import vTitle from '@/components/v-title';
    import vFlags from '@/components/v-flags';
    import vSelectCountries from '@/components/v-select-countries';
    import Repeater from '@/components/repeater/repeater';
    import vInlineNotifications from '@/components/v-inline-notifications';
    import {
        vCancel, vFormTile, vIconButton, vIcon, vErrorMessages,
    } from '@/components/form';
    import ShippingZoneModel from '@/common/models/shipping-zone-model';
    import FormLoaderMixin from '@/mixins/form-loader-mixin';
    import sluggifyMixin from '@/mixins/sluggify-mixin';
    import {required, minLength} from 'vuelidate/lib/validators';
    import RatesModal from './components/shipping-zones-rates-modal';

    export default {
        components: {
            vTitle,
            vFlags,
            vSelectCountries,
            Repeater,
            vInlineNotifications,
            vCancel,
            vFormTile,
            vIconButton,
            vIcon,
            vErrorMessages,
            RatesModal,
        },
        mixins: [
            FormLoaderMixin,
            sluggifyMixin({
                target: 'shippingZone.id',
                source: 'shippingZone.name',
                entityCallback(id) {
                    return this.$api(({shippingZones}) => shippingZones.get({id}));
                },
            }),
        ],
        data() {
            return {
                showCountriesModal: false,
                showRatesModal: false,
                showEditRateModal: false,
                isLoading: false,
                shippingZone: new ShippingZoneModel(),
                rateToEdit: null,
                repeatMessage: false,
                restOfTheWorld: null,
                otherZones: null,
            };
        },
        validations() {
            const validation = {
                shippingZone: {
                    id: {
                        required,
                    },
                    name: {
                        required,
                    },
                    rates: {
                        required,
                        minLength: minLength(1),
                    },
                },
            };
            if (!this.isRestOfTheWorld) {
                validation.shippingZone.countries = {
                    required,
                    minLength: minLength(1),
                };
            }
            return validation;
        },
        computed: {
            isDataRequired() {
                return !this.isDataReady;
            },
            isRestOfTheWorld() {
                return this.shippingZone.countries === null;
            },
            selectedCountriesFull() {
                if (!this.isRestOfTheWorld) {
                    return COUNTRIES.filter(country => this.shippingZone.countries.includes(country.countryCode));
                }
                return [];
            },
            breadcrumb() {
                return this.isFormUpdating
                    ? {
                        label: this.shippingZone.name,
                        route: {
                            name: 'edit-shipping-zone',
                            params: {
                                id: this.shippingZone.id,
                            },
                        },
                    }
                    : {
                        label: 'Add Shipping Zone',
                        route: {
                            name: 'add-shipping-zone',
                        },
                    };
            },
        },
        methods: {
            displayNoLimit(value, currencyCode) {
                return value ? this.$filters.monetizeExplicit(value, currencyCode) : 'No Limit';
            },
            addShippingRate(rate) {
                const existingRate = !!this.rateToEdit && this.shippingZone.rates.find(item => item.$internalId === this.rateToEdit.$internalId);
                if (existingRate) {
                    this.shippingZone.rates[this.shippingZone.rates.indexOf(existingRate)] = rate;
                    this.rateToEdit = null;
                } else {
                    this.shippingZone.rates.push(rate);
                }
            },
            removeRestOfTheWorld() {
                this.shippingZone.countries = [];
            },
            removeShippingRate(rate) {
                this.shippingZone.rates = this.shippingZone.rates.filter(item => item.$internalId !== rate.$internalId);
            },
            editShippingRate(rate) {
                this.rateToEdit = rate;
                this.showModal('EditRate');
            },
            updateSelectedCountries(countries) {
                this.shippingZone.countries = countries;
            },
            removeSelectedCountry(country) {
                this.updateSelectedCountries(
                    this.shippingZone.countries.filter(code => code !== country.countryCode),
                );
            },
            showModal(modalName) {
                this[`show${modalName}Modal`] = true;
            },
            closeModal(modalName) {
                this[`show${modalName}Modal`] = false;
            },
            resetForm() {
                this.repeatMessage = true;
                this.serverError = null;
                this.shippingZone = new ShippingZoneModel();
                this.$v.$reset();
            },
            async getAllZones() {
                try {
                    const {items} = await this.$api(({shippingZones}) => shippingZones.getAll());
                    this.otherZones = items
                        .filter(item => item.fields.id !== this.id)
                        .map(item => new ShippingZoneModel(item.fields));
                } catch (error) {
                    this.$toast.fromError(error);
                }
            },
            async fetchData() {
                const {id} = this;
                await this.getAllZones();
                if (this.isFormUpdating) {
                    try {
                        const {fields} = await this.$api(({shippingZones}) => shippingZones.get({id}));
                        this.shippingZone = new ShippingZoneModel(fields);
                    } catch (error) {
                        this.$toast.fromError(error, 'Could not load shipping zone. Please try again or contact support');
                        this.$router.push({name: 'shipping-zones'});
                    }
                }
            },
            async save() {
                this.isLoading = true;
                this.$v.$touch();
                if (this.$v.$error) {
                    this.isLoading = false;
                } else {
                    try {
                        if (this.isFormUpdating) {
                            await this.$api(({shippingZones}) => shippingZones.update({
                                id: this.shippingZone.id,
                                data: this.shippingZone.getPayload(),
                            }));
                            this.$router.push({name: 'shipping-zones'});
                        } else {
                            await this.$api(({shippingZones}) => shippingZones.create({
                                data: this.shippingZone.getPayload(),
                            }));
                            this.resetForm();
                        }
                        this.$toast.positive(`Shipping zone ${this.isFormUpdating ? 'updated.' : 'created.'}`);
                    } catch (error) {
                        this.serverError = error;
                    } finally {
                        this.isLoading = false;
                    }
                }
            },
        },
    };
</script>
